import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Brain, AlertTriangle, RefreshCw, CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { apiGet, apiPost } from "@/lib/api";
import { toast } from "sonner";
import SectorVerdictPill, { sectorVerdictTheme } from "@/components/common/SectorVerdictPill";

// Test 6 Fix 4 — bold tile colours so OVER/NEUT/UNDER are
// immediately distinguishable across the sector grid. The verdict
// badge inside each tile uses the canonical SectorVerdictPill so it
// matches every other place in the app (screener, stock detail, cards).
const VERDICT_STYLES = {
  OVERWEIGHT: {
    border: "border-[hsl(142_72%_36%)]/45",
    text: "text-[hsl(142_72%_29%)]",
    bg: "bg-[hsl(142_72%_36%)]/12",
    label: "OVERWEIGHT",
  },
  NEUTRAL: {
    border: "border-border",
    text: "text-muted-foreground",
    bg: "bg-muted/40",
    label: "NEUTRAL",
  },
  UNDERWEIGHT: {
    border: "border-[hsl(0_72%_50%)]/45",
    text: "text-[hsl(0_72%_42%)]",
    bg: "bg-[hsl(0_72%_50%)]/10",
    label: "UNDERWEIGHT",
  },
};

/**
 * SectorScoresGrid — Layer 6 Sector Context display.
 * Used on Market Intelligence page (and reusable elsewhere).
 *
 * Props:
 *  - isAdmin: bool — show "Refresh now" button
 *  - compact: bool — render in compact mode (smaller cards)
 */
const SectorScoresGrid = ({ isAdmin = false, compact = false }) => {
  const [items, setItems] = useState([]);
  const [lastRun, setLastRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const resp = await apiGet("/sectors/scores");
      setItems(resp?.items || []);
      setLastRun(resp?.last_run || null);
    } catch (e) {
      console.warn(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleRefresh = async () => {
    if (refreshing) return;
    if (
      !window.confirm(
        "Run Layer 6 sector analysis NOW? This calls Claude ~20 times (~$1) and takes 60-90 seconds. Continue?"
      )
    ) return;
    setRefreshing(true);
    try {
      await apiPost("/admin/sector-analysis/refresh", {});
      toast.success("Sector analysis started — refresh this page in ~90 seconds");
    } catch (e) {
      toast.error(`Sector refresh failed: ${e.message || e}`);
    } finally {
      setRefreshing(false);
    }
  };

  const summary = useMemo(() => {
    const out = { OVERWEIGHT: 0, NEUTRAL: 0, UNDERWEIGHT: 0 };
    items.forEach((row) => {
      const v = (row.overall_verdict || "NEUTRAL").toUpperCase();
      if (out[v] != null) out[v] += 1;
    });
    return out;
  }, [items]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-28" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <Card className="border-dashed bg-muted/10" data-testid="sector-scores-empty">
        <CardContent className="p-6 text-center space-y-3">
          <Brain className="h-8 w-8 text-muted-foreground mx-auto" />
          <div className="text-sm font-medium">Sector Context (Layer 6) not run yet</div>
          <CardDescription className="text-xs max-w-md mx-auto">
            Claude analyses every CSE sector once a week (Sundays 21:00 SLT) on cycle position,
            valuation, macro tailwinds, and foreign-investor sentiment. Each stock's COMB score
            picks up a 0-10 sector contribution from the latest run.
          </CardDescription>
          {isAdmin && (
            <Button
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="sector-scores-refresh-empty"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
              Run sector analysis now
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="sector-scores-grid">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap text-xs">
          <Badge variant="outline" className="border-success/40 text-success" data-testid="sector-summary-overweight">
            OVERWEIGHT · {summary.OVERWEIGHT}
          </Badge>
          <Badge variant="outline" className="border-border text-muted-foreground" data-testid="sector-summary-neutral">
            NEUTRAL · {summary.NEUTRAL}
          </Badge>
          <Badge variant="outline" className="border-destructive/40 text-destructive" data-testid="sector-summary-underweight">
            UNDERWEIGHT · {summary.UNDERWEIGHT}
          </Badge>
          {lastRun?.completed_at && (
            <span className="text-muted-foreground tabular-nums">
              Last run: {new Date(lastRun.completed_at).toLocaleString()}
              {lastRun.total_cost_usd != null && ` · $${Number(lastRun.total_cost_usd).toFixed(3)}`}
            </span>
          )}
        </div>
        {isAdmin && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="sector-scores-refresh-btn"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh now
          </Button>
        )}
      </div>

      <div
        className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ${
          compact ? "" : "xl:grid-cols-4"
        } gap-3`}
      >
        {items.map((row) => {
          const v = (row.overall_verdict || "NEUTRAL").toUpperCase();
          const theme = sectorVerdictTheme(v);
          return (
            <Link
              key={row.sector}
              to={`/stocks?sector=${encodeURIComponent(row.sector)}`}
              className={`group rounded-lg ${theme.boxBg} ${theme.boxBorder} p-3 space-y-2 hover:shadow-md transition-[box-shadow] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary`}
              data-testid={`sector-score-card-${row.sector?.replace(/\s+/g, "-")}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold truncate group-hover:text-primary transition-colors">
                  {row.sector}
                </div>
                <SectorVerdictPill verdict={v} size="sm" />
              </div>
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                <span className="font-mono tabular-nums text-foreground/90 font-semibold">
                  {row.total_score}/{row.max_score || 10}
                </span>
                {row.is_stale && (
                  <span className="inline-flex items-center gap-0.5 text-warning">
                    <AlertTriangle className="h-3 w-3" /> stale
                  </span>
                )}
                <span>· {row.stocks_in_sector || 0} cos</span>
              </div>
              <div className={`text-[10.5px] leading-snug line-clamp-2 ${theme.boxText}`}>
                {row.sector_summary || "—"}
              </div>
              <div className="grid grid-cols-3 gap-1 text-[10px] text-muted-foreground tabular-nums pt-1 border-t border-border/30">
                <div>
                  <span className="block text-[9px] uppercase tracking-wider">Cycle</span>
                  <span className="text-foreground/80 font-medium">
                    {row.cycle_score ?? 0}/3
                  </span>
                </div>
                <div>
                  <span className="block text-[9px] uppercase tracking-wider">Valuation</span>
                  <span className="text-foreground/80 font-medium">
                    {row.valuation_score ?? 0}/3
                  </span>
                </div>
                <div>
                  <span className="block text-[9px] uppercase tracking-wider">Macro</span>
                  <span className="text-foreground/80 font-medium">
                    {row.macro_score ?? 0}/2
                  </span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default SectorScoresGrid;
